import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewsQuery } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import type { IRecordListModel, ISavedView } from '@platform/core/types';
import type { IViewSettingsState } from './IViewSettingsState';
import { kindOfView } from './kindOfView';
import { viewSettingsRequest } from './viewSettingsRequest';
import { useSelectSavedView } from './useSelectSavedView';
import { shownColumns } from '../../views/grid/shownColumns';
import { draftOf } from './viewDraft';

/**
 * Панель «Налаштування подання» сторінки таблиці: «Редагувати подання» відкриває вибране подання (без вибору — нове з поточного стану: показані
 * стовпці в порядку показу, сховані за шириною — невибрані, як у прототипі v4), «Нове подання» — нове з усіма стовпцями списку, сортуванням
 * і розміром сторінки; збережене подання вибирається (і відкриває своє подання),
 * видалене — скидає вибір до типового. Запит із подання (viewSettingsRequest, примітка про сховані стовпці) відкриває панель вибраного подання.
 */
export function useViewSettings(model: IRecordListModel) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const select = useSelectSavedView(model);
  const { data: views = [] } = useViewsQuery({ ws: model.ws, recordType: model.rt.key });
  const [open, setOpen] = useState<IViewSettingsState | null>(null);
  const { state, columns } = model;
  const base = { kind: kindOfView(state.view), columns, sort: state.sort, pageSize: state.pageSize };
  const openFor = (view: ISavedView | undefined, columns = base.columns) => setOpen({ view, initial: draftOf(view, { ...base, columns }, t('listbar.newView'), model.rt) });
  const onEdit = () => {
    const shown = shownColumns.get(model.rt.key);
    openFor(views.find((v) => v.id === state.savedViewId), shown ? [...columns.filter((c) => c === 'title'), ...shown] : columns);
  };
  const edit = useRef(onEdit);
  edit.current = onEdit;
  useEffect(() => viewSettingsRequest.subscribe(() => edit.current()), []);
  const onNew = () => openFor(undefined);
  const close = useCallback(() => setOpen(null), []);
  const onSaved = (view: ISavedView) => {
    setOpen(null);
    select(view);
    dispatch(toastShown({ kind: 'success', text: t('listbar.viewSaved', { name: view.name }) }));
  };
  const onDeleted = () => {
    const name = open?.view?.name ?? '';
    setOpen(null);
    select(null);
    dispatch(toastShown({ kind: 'success', text: t('viewSettings.deleted', { name }) }));
  };
  return { open, onEdit, onNew, close, onSaved, onDeleted };
}
